import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  CardBody,
  Checkbox,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { accountApi } from '@netiva/classifieds-api';
import { usePropChange } from '@netiva/classifieds-common';
import { SelectableCard } from '@netiva/classifieds-ui';

import { normalizeProductGroupName, useProductGroups } from '@/hooks';
import { ProductGroups } from '@/constants';
import { Product } from '../types';
import { SelectableRadio } from './SelectableRadio';
import { TopListingBadge } from './TopListingBadge';
import { TopListingBenefits } from './TopListingBenefits';
import { TopListingNotification } from './TopListingNotification';
import { Price } from './Price';

export type CreateModalProps = Omit<ModalProps, 'children'> & {
  onSuccess?: () => void;
  products: Product[];
};

export const CreateModal: FC<CreateModalProps> = ({ onSuccess, products, isOpen, onClose, ...rest }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { orderedProductGroups } = useProductGroups(products);

  const isTopListingProduct = orderedProductGroups.some(
    ([group]) => group === normalizeProductGroupName(ProductGroups.MemberTopPlacement)
  );

  const [selectedProduct, setSelectedProduct] = useState<Product>();
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [createContract, { isLoading: isUpgradingContract }] = accountApi.useCreateContract();

  if (usePropChange(isOpen)) {
    setSelectedProduct(undefined);
    setIsConfirmed(false);
  }

  const handleSubmit = async () => {
    if (!selectedProduct || !isConfirmed) {
      return;
    }

    try {
      await createContract({
        accountContractsCreateContractRequest: { productId: selectedProduct.id },
      }).unwrap();
      toast({ status: 'success', description: t('products.createModal.success') });
      onClose();
      onSuccess?.();
    } catch (err) {
      console.error(err);
      toast({ status: 'error', description: t('products.createModal.error') });
    }
  };

  return (
    <Modal isCentered scrollBehavior="inside" size="xl" isOpen={isOpen} onClose={onClose} {...rest}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text>{t('products.createModal.title')}</Text>
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody>
          <Accordion
            defaultIndex={orderedProductGroups.length === 1 ? 0 : undefined}
            allowToggle={orderedProductGroups.length > 1}
          >
            {orderedProductGroups.map(([group, products]) => (
              <AccordionItem key={group}>
                <AccordionButton cursor={orderedProductGroups.length === 1 ? 'default' : undefined}>
                  <VStack align="stretch" textAlign="start" py={1}>
                    <Heading size="md">{t(`ad.steps.publishing.productGroups.${group}.title`, '')}</Heading>
                    <Text>{t(`ad.steps.publishing.productGroups.${group}.description`, '')}</Text>
                  </VStack>
                  {orderedProductGroups.length > 1 && <AccordionIcon ml="auto" boxSize={8} />}
                </AccordionButton>
                <AccordionPanel>
                  <VStack align="stretch" spacing={4}>
                    {isTopListingProduct && <TopListingNotification />}
                    {products.map((p) => {
                      return (
                        <Box key={p.id} position="relative">
                          {p.numberOfAds === 5 && <TopListingBadge />}
                          <SelectableCard
                            isSelected={p.id === selectedProduct?.id}
                            onClick={() => setSelectedProduct(p)}
                          >
                            <CardBody p={0}>
                              <Flex justifyContent="space-between" gap={2} align="flex-start">
                                <Flex gap={3} align="center">
                                  <SelectableRadio isSelected={p.id === selectedProduct?.id} />
                                  <Text fontSize="lg" fontWeight="bold">
                                    {p.name}
                                  </Text>
                                </Flex>
                                <Price price={p.price} />
                              </Flex>
                              {p.description && !p.description.startsWith('#') && (
                                <Text dangerouslySetInnerHTML={{ __html: p.description }} mt={2} />
                              )}
                            </CardBody>
                          </SelectableCard>
                        </Box>
                      );
                    })}
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </ModalBody>
        <ModalFooter display="block">
          {/* <Text mb={4}>{t('products.createModal.footer')}</Text> */}
          {isTopListingProduct && <TopListingBenefits />}
          <Checkbox isChecked={isConfirmed} onChange={(e) => setIsConfirmed(e.target.checked)} mb={6}>
            {t('products.createModal.termsConfirmation')}
          </Checkbox>
          <Flex>
            <Button onClick={onClose} colorScheme="neutral">
              {t('ui.buttons.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              isLoading={isUpgradingContract}
              isDisabled={!selectedProduct || !isConfirmed}
              ml="auto"
            >
              {selectedProduct?.numberOfAds
                ? t('products.createModal.submitWithCount', { count: selectedProduct.numberOfAds })
                : t('products.createModal.submit')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
